/**
 * Personalisation — what fills `{{1}}`, on the campaign that chose an audience.
 *
 * ## Why this is on the campaign and not on the create form
 *
 * A binding belongs to the campaign, not the template: `whatsapp.ts` settled
 * that in slice 7 and nothing here reopens it. But it also belongs *after* a
 * segment exists, because the only number that makes a binding safe is a count
 * over that segment — how many of the people this campaign reaches have nothing
 * in the column somebody just picked. The create form has a segment id and no
 * campaign to compute against; this screen has both, and it is also where
 * readiness and the schedule refusal already live. So the form creates the
 * draft and lands here, which is the navigation it already did.
 *
 * `NewCampaignPage` is therefore untouched by this slice. Its channel picker
 * appeared because `createCampaignRequestSchema` widened and its probe asked
 * the contract — the mechanism slice 7 left behind, proved rather than
 * replaced.
 *
 * ## The failure this panel exists to make visible
 *
 * Misspell `extras.Branch` as `extras.Branch Name` and **nothing goes wrong**.
 * Every contact resolves empty, every position takes its fallback, every
 * message sends, every counter is green, and 1,284 people are told "your
 * branch". There is no error anywhere in that sequence, which is precisely why
 * a screen has to carry the number: the cohort chip on each row says how many
 * of the reachable people would fall back on that position, counted over this
 * campaign's own segment, before anything is scheduled. A misspelling shows up
 * as a chip reading the whole audience.
 *
 * `preview.cohort` is `null` when the segment reaches nobody on WhatsApp, and
 * then **no chip is drawn at all**. Zero people falling back and no one to
 * count are different facts, and a `0` standing in for the second would be the
 * defect this codebase hunts hardest.
 *
 * ## A count is drawn only against the rows it was counted over
 *
 * `useAsyncData` keeps `data` across a reload **and across an error**, on
 * purpose — a list that blanked on every keystroke would be unusable. That
 * makes the previous answer survive three states this screen has to tell
 * apart: the debounce window after a keystroke, where `asked.key` still names
 * the old bindings; the round trip itself; and a failed round trip, where the
 * old answer would otherwise sit there for ever with nothing saying so.
 *
 * Correct `extras.Branch` to a misspelt `extras.Brnach` and a chip that did
 * not check would keep reading the old reassuring "0 of 1,284 fall back"
 * against the new field — the one sentence this chip exists to make
 * impossible. So `previewIsCurrent` gates it: the chip is drawn only when the
 * request that produced the number was made for the rows now on screen and it
 * succeeded. Everything else draws the spinner beside the bubble, and a
 * failure draws its error instead of the last picture that worked.
 *
 * ## Every state a row can be in wears a marker, and the loudest is the one
 * that blocks a send
 *
 * Slice 49's design review rendered this panel and found the volume inverted.
 * A leftover binding the send ignores had an amber chip. An answered row had a
 * count. **A required position with nothing bound had its token and nothing
 * else** — and that is the only one of the three that stops the campaign
 * going out, with its single statement 1,100px further down the page in the
 * Schedule panel at 390px. So `no field` is drawn, `ignored` is demoted to
 * neutral, and the cohort chip takes the colour that was being spent on advice.
 *
 * Both new chips are `=== true` / `=== null` and never a truthiness test, for
 * the reason `wa-binding-rows.ts` gives at length: on an unreadable template
 * every row is `required: null`, and both "the template needs this" and "the
 * template dropped this" are then claims about a document nobody opened. That
 * row says `template unread`, and the label above the list stops calling them
 * the template's variables at all.
 *
 * ## Nothing here substitutes anything, and nothing here is folded away
 *
 * The preview is a request. The API resolves it with `waValuesForRecipient` and
 * assembles it with the send's own functions, against a real contact from this
 * campaign's segment, and `WaBoundPreview` draws the finished text. A
 * substitution loop written in the browser would be right on screen and wrong
 * on the wire, and the drift would be discovered by the recipients. See
 * `WaBoundPreview`'s header for the whole of that argument.
 *
 * It is rendered **inline**, under the rows. It sat in a `MoreDetail` until the
 * same review: closed by default, below Save, its summary 1,043px down a
 * 1,071px card at 390px. `MoreDetail` is for the third kind of text and its own
 * header says outright that anything a person could draw a false conclusion
 * without does not belong in one. This is that thing.
 *
 * ## Editing stops where `isCampaignEditable` stops
 *
 * A draft can be bound; anything else is read-only. Once a campaign is
 * scheduled its recipient list may already exist, and changing what fills
 * `{{1}}` underneath a materialised list produces messages nobody chose to
 * send. Cancel, edit, schedule again — the same three deliberate steps the
 * template and the segment take, for the same reason. The **preview stays** on
 * every status: seeing what a campaign that has already sent put in `{{1}}` is
 * worth as much as seeing it beforehand.
 *
 * ## No explanatory paragraph, anywhere on it
 *
 * A chip per position, a picker, a box, a count, a picture. Everything that
 * argues for those numbers is in this header or in `WaBoundPreview`'s. The
 * refusals are the only sentences, they fire only after somebody has pressed
 * Save, and each one names the position by the token printed on its own row.
 */
import {
  isCampaignEditable,
  updateCampaignRequestSchema,
  type Campaign,
  type CampaignWaPreview,
} from '@wend/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Kicker,
  Select,
  Spinner,
  useToast,
} from '@wend/ui';
import { Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { previewWhatsAppCampaign, updateCampaign } from '../../lib/api/campaigns';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import { validate, type FormErrors } from '../../lib/form';
import { formatCount } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';
import { WaBoundPreview } from './WaBoundPreview';
import {
  EXTRAS_SOURCE,
  WA_BINDING_SOURCE_OPTIONS,
  placeBindingErrors,
  refusalsForRows,
  rowErrorKey,
  rowsForTemplate,
  sourceForRow,
  submissionForRows,
  waPositionToken,
  type WaBindingRow,
} from './wa-binding-rows';

/**
 * How long a keystroke waits before the preview is asked again.
 *
 * The preview is a server round trip that resolves against a real contact, so
 * it is not run per character. It is not run per *save* either: an unsaved
 * binding is exactly the one somebody wants to look at, and a preview that
 * could only show what is already stored would be a picture of the previous
 * decision.
 */
const PREVIEW_DEBOUNCE_MS = 500;

/**
 * What colour the cohort chip is, and it is three colours because the chip
 * carries three different facts.
 *
 * It was two — amber above zero, neutral at zero — and slice 49's design
 * review measured what that produced on the page: "1,284 of 1,284 fall back"
 * and "311 of 1,284 fall back" rendered as the same amber pill, so the
 * signature of a misspelt column and an ordinary gappy list differed only by
 * arithmetic the reader had to do. **Everyone falling back is the failure this
 * whole panel exists to make visible**, so it takes the tone this product uses
 * for a failure.
 *
 * `reachable > 0` is not belt and braces. `fallbackCount === reachable` is
 * trivially true at 0 of 0, and a `bad` chip there would be the codebase's
 * oldest defect — nothing measured rendered as the worst possible reading.
 *
 * Exported because it is the one branch on this screen that is worth running
 * rather than grepping: the ordering of its three cases is what the review
 * found wrong, and a probe that reads the source proves the characters rather
 * than the decision.
 */
export function cohortChipTone(fallbackCount: number, reachable: number): 'neutral' | 'warn' | 'bad' {
  if (fallbackCount === 0) return 'neutral';
  if (fallbackCount === reachable && reachable > 0) return 'bad';
  return 'warn';
}

export interface CampaignWhatsAppPersonalisationProps {
  campaign: Campaign;
  /** Re-reads the campaign — the template positions come with it. */
  onRetry: () => void;
  /**
   * The save landed. **It hands back nothing, on purpose.**
   *
   * It used to pass the campaign the PATCH returned, and the one call site
   * ignored it and re-read instead — correctly. Readiness is computed by the
   * server from the template's positions and the stored bindings together, and
   * it does not travel on the campaign, so a page that patched itself from this
   * argument would leave Schedule disabled by a blocker that no longer exists,
   * with nothing on screen to explain it. A parameter whose only correct use is
   * to be ignored is a parameter the next caller will use.
   */
  onSaved: () => void;
}

/**
 * The gate, and it is a **mount** gate rather than a branch inside the panel.
 *
 * `whatsapp === null` says this is an email campaign, and an email campaign
 * must not so much as *ask* for a WhatsApp preview: a hook that runs before an
 * early return still runs, so a `return null` at the top of the panel would
 * leave every email campaign detail page posting to
 * `/campaigns/:id/whatsapp-preview` on load and being refused. Not mounting it
 * is the only version of "nothing happens" that is actually nothing.
 */
export function CampaignWhatsAppPersonalisation(props: CampaignWhatsAppPersonalisationProps) {
  const whatsapp = props.campaign.whatsapp;
  if (whatsapp === null || whatsapp === undefined) return null;
  return <Personalisation {...props} whatsapp={whatsapp} />;
}

function Personalisation({
  campaign,
  onRetry,
  onSaved,
  whatsapp,
}: CampaignWhatsAppPersonalisationProps & {
  whatsapp: NonNullable<Campaign['whatsapp']>;
}) {
  const { toast } = useToast();

  /*
   * THREE DIFFERENT FACTS, AND NONE OF THEM IS ZERO.
   *
   *   `whatsapp === null`          this is not a WhatsApp campaign at all
   *   `whatsapp.template === null` its template could not be read, so nothing
   *                                is known about which positions it needs
   *   `requiredPositions === []`   it was read, and it needs none
   *
   * The first draws nothing. The second draws the rows that *are* stored plus
   * the failure and its retry — never "this template has no variables", which
   * is a claim about a document nobody managed to open, and **never an
   * `ignored` chip either**: `rowsForTemplate` is handed `null` rather than an
   * empty list, so those rows come back `required: null` and no chip is drawn
   * on them. It drew one on every row until slice 49's review, which is the
   * same collapse this comment forbids, made in code. The third draws no rows,
   * which is what most utility templates look like.
   */
  const template = whatsapp.template;
  const bindings = whatsapp.bindings;

  const editable = isCampaignEditable(campaign.status);

  /*
   * The rows are seeded from the campaign and re-seeded when the campaign's own
   * answer changes — never on every render. This screen polls, so a plain
   * `useState(initial)` would be right and a `useState` re-initialised from
   * props would wipe a half-typed fallback on the next tick. The key is the two
   * facts the rows are built from, so an edit survives every poll that did not
   * change them and a template that grew a position rebuilds the list.
   */
  const seed = JSON.stringify({ positions: template?.requiredPositions ?? null, bindings });
  const [rows, setRows] = useState<WaBindingRow[]>(() =>
    rowsForTemplate(template?.requiredPositions ?? null, bindings),
  );
  const [seeded, setSeeded] = useState(seed);
  if (seeded !== seed) {
    setSeeded(seed);
    setRows(rowsForTemplate(template?.requiredPositions ?? null, bindings));
  }

  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  /*
   * What the preview was last asked about — a value, not a signal, so the
   * request and the picture on screen can never be about different bindings.
   * Only rows that would survive `waBindingListSchema` are sent: a row with no
   * fallback yet is left out, and the position then draws in the bubble as the
   * literal `{{n}}` that would actually reach a phone.
   */
  const previewable = submissionForRows(rows).bindings.filter(
    (binding) => binding.fallback.trim() !== '',
  );
  const previewKey = JSON.stringify(previewable);
  const [asked, setAsked] = useState(() => ({ key: previewKey, bindings: previewable }));

  // The array itself, not re-derived from the key: a round trip through JSON
  // would give the request a value that merely looks like the one on screen,
  // and "merely looks like" is the whole family of defect this panel is here
  // to prevent.
  const latest = useRef(previewable);
  latest.current = previewable;

  useEffect(() => {
    if (asked.key === previewKey) return;
    const timer = setTimeout(() => {
      setAsked({ key: previewKey, bindings: latest.current });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [previewKey, asked.key]);

  const preview = useAsyncData<CampaignWaPreview>(
    (signal) => previewWhatsAppCampaign(campaign.id, { bindings: asked.bindings }, signal),
    [campaign.id, asked.key],
  );

  /*
   * WHICH BINDINGS THE ANSWER ON SCREEN IS ABOUT.
   *
   * `asked.key` is what the request was made for; `previewKey` is what the
   * rows say right now. Between a keystroke and the debounce firing the two
   * differ while the hook is still `ready` over the previous answer, and
   * neither `preview.data` nor `preview.status` alone can tell that apart from
   * a fresh one. Both together can.
   */
  const previewIsCurrent = asked.key === previewKey && preview.status === 'ready';

  /** An answer for these rows is still coming, and there is no error to draw. */
  const previewSettling = !previewIsCurrent && preview.status !== 'error';

  const cohort = previewIsCurrent ? (preview.data?.cohort ?? null) : null;

  function patchRow(position: number, patch: Partial<WaBindingRow>): void {
    setRows((current) =>
      current.map((row) => (row.position === position ? { ...row, ...patch } : row)),
    );
    // Every refusal on this row is about what it held a moment ago, and a
    // form-level one is stale the instant anything is edited.
    setErrors((current) => {
      const next = { ...current };
      delete next[rowErrorKey(position, 'source')];
      delete next[rowErrorKey(position, 'column')];
      delete next[rowErrorKey(position, 'fallback')];
      delete next[ROOT_FIELD];
      delete next.waBindings;
      return next;
    });
  }

  async function onSave(): Promise<void> {
    if (pending) return;

    setRequestId(undefined);

    // The one thing the contract cannot phrase, checked first: an `extras.`
    // with no column. An unbound position is NOT refused here — it is left out
    // of the submission, and the schedule is what refuses it. See
    // `wa-binding-rows.ts`.
    const refusals = refusalsForRows(rows);
    if (Object.keys(refusals).length > 0) {
      setErrors((current) => ({ ...current, ...refusals }));
      return;
    }

    const { bindings: submitted, positions } = submissionForRows(rows);
    const result = validate(updateCampaignRequestSchema, { waBindings: submitted });
    if (!result.ok) {
      setErrors(placeBindingErrors(result.errors, positions));
      return;
    }

    setErrors({});
    setPending(true);

    try {
      await updateCampaign(campaign.id, result.data);
      toast({
        tone: 'success',
        title: 'Personalisation saved',
        description: 'Nothing has been sent.',
      });
      onSaved();
      setPending(false);
    } catch (error) {
      setErrors({
        ...placeBindingErrors(apiFieldErrors(error), positions),
        [ROOT_FIELD]: errorMessage(error),
      });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  /*
   * The positions whose `{{n}}` would reach Meta literally.
   *
   * `=== true` and never `!== false`. On an unreadable template every row is
   * `required: null`, and a claim that the send will go out with a gap is a
   * claim about a document nobody opened — the collapse `wa-binding-rows.ts`
   * spends three paragraphs forbidding. A row the template dropped is not here
   * either: nothing fills it and nothing needs it.
   */
  const unfilledPositions = rows
    .filter((row) => row.required === true && sourceForRow(row) === null)
    .map((row) => row.position);

  /*
   * A template that WAS read and needs nothing. `rows.length === 0` alone is
   * also true of an unreadable template with no stored bindings, where this
   * chip would be the exact sentence this panel's header forbids — a claim
   * about a document nobody managed to open. The `template !== null` is what
   * makes it a measurement rather than a guess.
   */
  const noVariables = template !== null && rows.length === 0;

  return (
    <Card>
      <CardHeader>
        {/* The emptiness is stated rather than left to be inferred from a
            titled box with nothing in it. Most utility templates render this
            card, and "no variables" and "this panel failed to draw" look
            identical without it. */}
        <CardTitle className="flex flex-wrap items-center gap-2">
          Personalisation
          {noVariables && <Badge tone="neutral">no variables</Badge>}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {errors[ROOT_FIELD] !== undefined && (
          <FormAlert tone="error" requestId={requestId}>
            {errors[ROOT_FIELD]}
          </FormAlert>
        )}

        {/* A refusal about the list as a whole — it names no row, so it cannot
            be drawn beside a control, and a refusal that cannot be placed is
            still drawn rather than dropped. */}
        {errors.waBindings !== undefined && <FormAlert tone="error">{errors.waBindings}</FormAlert>}

        {/* The campaign read succeeded and the template document did not, so
            there is no error object to carry — the fact IS the null. What must
            never appear here is "this template has no variables", which is a
            claim about a document nobody managed to open. */}
        {template === null && (
          <ErrorState
            error={null}
            title="Could not load this template"
            onRetry={onRetry}
            className="flex flex-col items-start gap-2 py-1"
          />
        )}

        {rows.length > 0 && (
          <div className="flex flex-col gap-3">
            {/* The label is a claim about where these rows came from, so it
                cannot be the same on the branch where nobody could read the
                template. There the rows are the stored bindings and nothing
                else, and "Variables in this template" would be the panel
                asserting the contents of a document it failed to open — state
                3 dressed as state 1. Each row says the same thing again with a
                `template unread` chip, because a reader who scrolled past the
                label is looking at the rows. */}
            <Kicker rule>
              {template === null ? 'Variables stored on this campaign' : 'Variables in this template'}
            </Kicker>
            <ol data-role="wa-binding-rows" className="flex flex-col gap-3">
              {rows.map((row) => (
                <BindingRow
                  key={row.position}
                  row={row}
                  errors={errors}
                  editable={editable}
                  pending={pending}
                  reachable={cohort?.reachable ?? null}
                  fallbackCount={
                    cohort?.fallbackByPosition.find((entry) => entry.position === row.position)
                      ?.count ?? null
                  }
                  onChange={patchRow}
                />
              ))}
            </ol>
          </div>
        )}

        {/* No rows, nothing to save. A template with no positions is the
            ordinary utility template, and a Save that could only ever write
            `[]` over `[]` is a control that does nothing. */}
        {editable && rows.length > 0 && (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={pending}
              onClick={() => {
                void onSave();
              }}
            >
              {pending ? 'Saving' : 'Save'}
            </Button>
          </div>
        )}

        {/* NOT BEHIND A DISCLOSURE. It was a `MoreDetail` until slice 49's
            design review rendered the panel: closed by default, below Save, and
            at 390px its summary sat 1,043px down a 1,071px card — the one thing
            this panel exists for, behind a click and a screen of scroll.
            `MoreDetail`'s own header rules it out in as many words: it is only
            ever for the third kind of text, and "anything a person could draw a
            false conclusion without is not allowed in here at all". The
            misspelt-column failure IS a false conclusion drawn from a screen
            that looked fine. The bubble is `max-w-[22rem]` and fits at 390px,
            so folding it bought nothing and cost the whole point.

            The error is asked FIRST, not last. `useAsyncData` keeps the last
            good answer through a failure, so a branch that reached for
            `preview.data` before the status left a refused preview drawing a
            confident bubble of the previous binding, with nothing on screen
            saying the request had failed and no way to retry it.

            The bubble does survive a reload, because that is what stops it
            blinking on every keystroke — but never silently: the spinner is
            drawn beside it for the whole of the debounce and the round trip,
            which is the same `refreshing` hint every other polling screen
            uses. */}
        <div className="flex flex-col gap-3">
          <Kicker rule>
            What one recipient sees
          </Kicker>
          <div className="flex flex-col gap-2">
            {preview.status === 'error' ? (
              <ErrorState
                error={preview.error}
                title="Could not build this preview"
                onRetry={preview.reload}
                className="flex flex-col items-start gap-2 py-1"
              />
            ) : (
              <>
                {previewSettling && (
                  <Spinner
                    size="sm"
                    label={
                      preview.data === null ? 'Building this preview' : 'Rebuilding this preview'
                    }
                  />
                )}
                {preview.data !== null && (
                  <WaBoundPreview
                    preview={preview.data}
                    unfilledPositions={unfilledPositions}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* One position                                                                */
/* -------------------------------------------------------------------------- */

/**
 * One row: the token, what fills it, and what fills it when the contact has
 * nothing.
 *
 * The chip is the token **as the template writes it** — `{{1}}`, not
 * "Position 1" — because it has to match what the reader sees in the bubble
 * below character for character. Two names for one thing on one screen is how
 * somebody binds the wrong position.
 *
 * "If empty" is not optional and there is no note saying so. `waValuesForRecipient`
 * substitutes the fallback exactly when the contact's value trims to empty, and
 * Meta refuses a message with an empty parameter — so an unanswered fallback is
 * a recipient that fails a third of the way through a send, on the tenant's own
 * WABA. Requiring the box moves that choice to the person composing, where it
 * costs one word. The contract refuses it too, in the same sentence, which is
 * why this file writes no sentence of its own about it.
 *
 * **There is no chip for "this column may be empty for some contacts".** Wend
 * has not measured that — extras differ per contact inside one segment and
 * nothing lists which keys exist — and a chip claiming it would be a
 * measurement nobody took. What *is* measured is the cohort count beside it,
 * which comes back from the preview over this campaign's own segment.
 */
function BindingRow({
  row,
  errors,
  editable,
  pending,
  reachable,
  fallbackCount,
  onChange,
}: {
  row: WaBindingRow;
  errors: FormErrors;
  editable: boolean;
  pending: boolean;
  /** Null when the segment reaches nobody on WhatsApp — never 0 for unknown. */
  reachable: number | null;
  /** Null for the same reason, and 0 means nobody falls back on this one. */
  fallbackCount: number | null;
  onChange: (position: number, patch: Partial<WaBindingRow>) => void;
}) {
  const token = waPositionToken(row.position);
  const extras = row.source === EXTRAS_SOURCE;
  const locked = !editable || pending;

  return (
    <li className="flex flex-col gap-3 rounded-base border border-line p-3">
      {/* `justify-between` and no `ml-auto` sent Remove to a third line at
          390px, left-aligned under the chips, where it reads as one of them.
          `ml-auto shrink-0` keeps it at the right edge on whichever line it
          lands on, and the chip group wraps beneath the token instead. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Badge tone="neutral" className="font-mono">
            {token}
          </Badge>

          {/* THE ONLY STATE ON THIS ROW THAT BLOCKS A SEND, AND IT WAS THE ONLY
              ONE WITH NO MARKER.

              A required position with nothing bound drew its token and nothing
              else, while a leftover binding the send ignores wore an amber chip
              and an answered row wore a count — the loudness exactly inverted.
              Its only statement was in the Schedule panel, ~1,100px further
              down at 390px, and `wa_bindings_incomplete` is a blocker.

              `=== true`, never `!== false`. On an unreadable template every row
              is `required: null`, and "the template needs this and nothing
              fills it" is then a claim about a document nobody opened. */}
          {row.required === true && sourceForRow(row) === null && (
            <Badge tone="warn">no field</Badge>
          )}

          {/* The template dropped this position and the binding outlived it.
              Advice, never a refusal: the send ignores it, and refusing a
              schedule over a leftover row would turn editing a template into a
              campaign that cannot go out. Toned `neutral` for exactly that
              reason — it was amber, the same amber as the cohort chip that is
              the signature of a misspelt column, which spends the one colour
              this row has on the one state that costs nothing.

              `=== false`, not `!row.required`. `null` is "the template could
              not be read", and a chip saying the send ignores this binding
              would be false there — the send will use it. That row says what is
              actually known about it instead. */}
          {row.required === false && <Badge tone="neutral">ignored</Badge>}

          {/* Nobody could read the template, so nothing here knows whether it
              still asks for this position. Said on the row as well as in the
              label above the list, because a reader scrolling the rows is past
              the label. */}
          {row.required === null && <Badge tone="neutral">template unread</Badge>}

          {/* THREE TONES, BECAUSE THIS CHIP CARRIES THREE DIFFERENT FACTS.
              Every one of the audience falling back is the misspelt-column
              signature — `bad`. Some of them is ordinary incompleteness in a
              list — `warn`. None of them is nothing to report — `neutral`. At
              one tone "1,284 of 1,284" and "311 of 1,284" differed only by
              arithmetic the reader had to do. `reachable > 0` keeps 0-of-0 out
              of `bad`: no one to count is not everyone falling back. */}
          {reachable !== null && fallbackCount !== null && (
            <Badge tone={cohortChipTone(fallbackCount, reachable)}>
              {formatCount(fallbackCount)} of {formatCount(reachable)} fall back
            </Badge>
          )}
        </div>

        {/* A row cannot be added — the positions are the template's — but a
            binding can be taken back off one, which is the only way to undo a
            field chosen by mistake and the only way to clear a binding the
            template no longer asks for. */}
        {editable && row.source !== null && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto shrink-0"
            disabled={pending}
            aria-label={`Remove the field for ${token}`}
            onClick={() => {
              onChange(row.position, { source: null, column: '', fallback: '' });
            }}
          >
            <Trash2 aria-hidden="true" className="size-4" />
            Remove
          </Button>
        )}
      </div>

      {/* ONE GRID FOR EVERY ROW. It was three columns on an extras row and two
          on the others, so "If empty" started at a different x on every row of
          the same list — x≈405 under `{{1}}`, x≈490 under `{{2}}` at 1440.
          The Column cell is held open rather than removed, and only from `sm`
          up: at base the grid is one column, where an empty cell would be a
          12px gap with nothing in it. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Filled by" error={errors[rowErrorKey(row.position, 'source')]}>
          <Select
            options={WA_BINDING_SOURCE_OPTIONS}
            value={row.source ?? undefined}
            placeholder="Choose a field"
            disabled={locked}
            onValueChange={(value) => {
              onChange(row.position, { source: value, column: '' });
            }}
          />
        </FormField>

        {extras ? (
          <FormField label="Column" error={errors[rowErrorKey(row.position, 'column')]}>
            <Input
              value={row.column}
              placeholder="Branch"
              disabled={locked}
              onChange={(event) => {
                onChange(row.position, { column: event.target.value });
              }}
            />
          </FormField>
        ) : (
          <div aria-hidden="true" className="hidden sm:block" />
        )}

        {/* `required` marks it, and `FormField` draws the asterisk and an
            sr-only "(required)". The box is refused blank by the contract and
            by Meta — a message with an empty parameter is rejected — and it was
            the one control on the row with no placeholder and no marker while
            both its neighbours had placeholders, so it read as the optional
            one. The marker is the whole statement; no sentence is added. */}
        <FormField
          label="If empty"
          required
          error={errors[rowErrorKey(row.position, 'fallback')]}
        >
          <Input
            value={row.fallback}
            maxLength={200}
            disabled={locked}
            onChange={(event) => {
              onChange(row.position, { fallback: event.target.value });
            }}
          />
        </FormField>
      </div>
    </li>
  );
}
